import { Alert, Breadcrumb, Button, Card, Descriptions, Input, Skeleton, Space, Table, Tag, Typography, App as AntApp } from "antd";
import { EditOutlined, StopOutlined, UserSwitchOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { api } from "../app/api.ts";
import { ApiError } from "../app/apiError.ts";
import { useAuth } from "../app/auth.tsx";
import { classKeys, formatDate, type ClassSubject, type TeacherAssignment } from "../app/classes.ts";
import { AssignTeacherModal } from "../components/AssignTeacherModal.tsx";
import { AuditHistory } from "../components/AuditHistory.tsx";
import { ClassRosterCard } from "../components/ClassRosterCard.tsx";

export function ClassDetailPage() {
  const { id = "" } = useParams();
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("academic.manage");
  const canReadAudit = hasPermission("audit.read");
  const [dialog, setDialog] = useState<{ subject: ClassSubject; mode: "assign" | "end" }>();
  const [renaming, setRenaming] = useState<string>();

  const details = useQuery({
    queryKey: classKeys.detail(id),
    queryFn: async () => {
      const { data, error } = await api.GET("/academic/classes/{id}", { params: { path: { id } } });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
  });
  const history = useQuery({
    queryKey: classKeys.teachers(id),
    queryFn: async () => {
      const { data, error } = await api.GET("/academic/classes/{id}/teachers", { params: { path: { id } } });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
  });

  const rename = useMutation({
    mutationFn: async (name: string) => {
      const { data, error, response } = await api.PUT("/academic/classes/{id}", { params: { path: { id } }, body: { name } });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: async () => {
      setRenaming(undefined);
      await Promise.all([queryClient.invalidateQueries({ queryKey: classKeys.all }), queryClient.invalidateQueries({ queryKey: ["audit"] })]);
    },
    onError: (err) => void message.error((err instanceof ApiError ? err.messages(t("app.error")) : [t("app.error")]).join(" ")),
  });

  if (details.isPending) return <Skeleton active />;
  if (details.isError || !details.data) return <Alert type="error" showIcon title={t("app.error")} />;
  const schoolClass = details.data;

  const subjectColumns = [
    { title: t("classes.subject"), dataIndex: "subjectName", key: "subjectName", ellipsis: true },
    {
      title: t("classes.kind"),
      dataIndex: "kind",
      key: "kind",
      width: 120,
      responsive: ["sm" as const],
      render: (kind: ClassSubject["kind"]) => <Tag color={kind === "Main" ? "blue" : "default"}>{t(`courses.kinds.${kind}`)}</Tag>,
    },
    {
      title: t("classes.teacher"),
      key: "teacher",
      render: (_: unknown, s: ClassSubject) =>
        s.teacher ? (
          <span>
            {s.teacher.fullName}
            <Typography.Text type="secondary">{t("classes.since", { date: formatDate(s.teacherSince) })}</Typography.Text>
          </span>
        ) : (
          <Typography.Text type="warning">{t("classes.noTeacher")}</Typography.Text>
        ),
    },
    ...(canManage
      ? [
          {
            title: t("common.actions"),
            key: "actions",
            width: 110,
            render: (_: unknown, s: ClassSubject) => (
              <Space size={0}>
                <Button type="text" icon={<UserSwitchOutlined />} aria-label={t("classes.assign")} title={t("classes.assign")} onClick={() => setDialog({ subject: s, mode: "assign" })} />
                {s.teacher && <Button type="text" danger icon={<StopOutlined />} aria-label={t("classes.end")} title={t("classes.end")} onClick={() => setDialog({ subject: s, mode: "end" })} />}
              </Space>
            ),
          },
        ]
      : []),
  ];

  const historyColumns = [
    { title: t("classes.subject"), dataIndex: "subjectName", key: "subjectName", ellipsis: true },
    { title: t("classes.teacher"), dataIndex: "teacherName", key: "teacherName", ellipsis: true },
    { title: t("classes.from"), dataIndex: "from", key: "from", width: 110, render: (d: string) => formatDate(d) },
    { title: t("classes.to"), dataIndex: "to", key: "to", width: 110, render: (d: string | null) => (d ? formatDate(d) : <Tag color="green">{t("classes.current")}</Tag>) },
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      <Breadcrumb items={[{ title: <Link to="/academic/classes">{t("classes.title")}</Link> }, { title: schoolClass.name }]} />
      <Card
        title={
          renaming === undefined ? (
            <Space>
              <Typography.Title level={3} style={{ margin: 0 }}>
                {schoolClass.name}
              </Typography.Title>
              {canManage && <Button type="text" icon={<EditOutlined />} aria-label={t("classes.rename")} onClick={() => setRenaming(schoolClass.name)} />}
            </Space>
          ) : (
            <Space.Compact>
              <Input aria-label={t("classes.name")} value={renaming} maxLength={16} onChange={(e) => setRenaming(e.target.value)} onPressEnter={() => rename.mutate(renaming)} />
              <Button type="primary" loading={rename.isPending} onClick={() => rename.mutate(renaming)}>
                {t("family.save")}
              </Button>
              <Button onClick={() => setRenaming(undefined)}>{t("family.cancel")}</Button>
            </Space.Compact>
          )
        }
        extra={<Link to={`/academic/classes/${schoolClass.id}/timetable`}><Button>{t("timetable.open")}</Button></Link>}
      >
        <Descriptions size="small" column={{ xs: 1, sm: 3 }}>
          <Descriptions.Item label={t("classes.year")}>{schoolClass.academicYearLabel}</Descriptions.Item>
          <Descriptions.Item label={t("classes.grade")}>{schoolClass.grade}</Descriptions.Item>
          <Descriptions.Item label={t("classes.course")}>{schoolClass.courseName}</Descriptions.Item>
        </Descriptions>
      </Card>
      <Card title={t("classes.subjectsTitle")}>
        <Table<ClassSubject> rowKey="subjectId" size="middle" columns={subjectColumns} dataSource={schoolClass.subjects} pagination={false} scroll={{ x: 480 }} locale={{ emptyText: t("classes.noSubjects") }} />
      </Card>
      <ClassRosterCard classId={schoolClass.id} />
      <Card title={t("classes.historyTitle")}>
        <Table<TeacherAssignment> rowKey="id" size="small" loading={history.isPending} columns={historyColumns} dataSource={history.data ?? []} pagination={false} scroll={{ x: 480 }} locale={{ emptyText: t("classes.noHistory") }} />
      </Card>
      {canReadAudit && (
        <Card title={t("audit.title")}>
          <AuditHistory entityType="SchoolClass" entityId={schoolClass.id} includeChildren />
        </Card>
      )}
      <AssignTeacherModal classId={schoolClass.id} subject={dialog?.subject} mode={dialog?.mode ?? "assign"} onClose={() => setDialog(undefined)} />
    </Space>
  );
}
